import { CodeBlock, InfoBox, SectionTitle, SubsectionTitle, Table, Badge } from './shared';

export function AuthContent() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-4">认证授权</h1>
        <p className="text-lg text-slate-400 leading-relaxed">
          OpenClaw 网关支持多种认证方式，可以根据不同的使用场景选择合适的认证策略。
        </p>
      </div>

      <SectionTitle id="auth-overview">认证方式概览</SectionTitle>
      <Table
        headers={['认证方式', '适用场景', '安全级别', '实现难度']}
        rows={[
          [<Badge variant="default">API Key</Badge>, '服务端到服务端', <Badge variant="warning">中</Badge>, <Badge variant="success">简单</Badge>],
          [<Badge variant="default">JWT</Badge>, '用户身份验证', <Badge variant="success">高</Badge>, <Badge variant="warning">中等</Badge>],
          [<Badge variant="default">OAuth 2.0</Badge>, '第三方应用集成', <Badge variant="success">高</Badge>, <Badge variant="error">复杂</Badge>],
          [<Badge variant="default">mTLS</Badge>, '企业级安全', <Badge variant="success">极高</Badge>, <Badge variant="error">复杂</Badge>]
        ]}
      />

      <SectionTitle id="api-key">API Key 认证</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        API Key 是最简单的认证方式，适合服务端到服务端的调用。
      </p>

      <SubsectionTitle id="api-key-config">配置 API Key 认证</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
auth:
  enabled: true
  type: "api_key"
  api_key:
    # 从哪个 HTTP Header 读取 API Key
    header: "Authorization"  # 或 "X-API-Key"
    # 前缀（如果使用 Authorization header）
    prefix: "Bearer"  # 或留空
    # API Key 存储方式
    storage: "file"  # file, database, redis
    # 文件存储路径
    file_path: "~/.openclaw/api-keys.json"`}
      />

      <SubsectionTitle id="api-key-management">管理 API Key</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 创建新的 API Key
openclaw gateway api-key create --name "My App" --expires 30d

# 输出示例
API Key: sk_live_abc123def456ghi789jkl012mno345pqr678stu901vwx234yz
Name: My App
Created: 2024-01-01T00:00:00Z
Expires: 2024-01-31T00:00:00Z

# 列出所有 API Key
openclaw gateway api-key list

# 查看 API Key 详情
openclaw gateway api-key show sk_live_abc123...

# 撤销 API Key
openclaw gateway api-key revoke sk_live_abc123...

# 更新 API Key（延长过期时间、修改权限等）
openclaw gateway api-key update sk_live_abc123... --expires 60d

# 查看 API Key 使用统计
openclaw gateway api-key stats sk_live_abc123...`}
      />

      <SubsectionTitle id="api-key-usage">使用 API Key</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 使用 Authorization Header
curl -X POST http://localhost:3000/api/chat \\
  -H "Authorization: Bearer sk_live_abc123..." \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Hello"}'

# 使用 X-API-Key Header
curl -X POST http://localhost:3000/api/chat \\
  -H "X-API-Key: sk_live_abc123..." \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Hello"}'`}
      />

      <SubsectionTitle id="api-key-permissions">权限控制</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        可以为每个 API Key 设置不同的权限：
      </p>
      <CodeBlock
        language="bash"
        code={`# 创建只读 API Key
openclaw gateway api-key create \\
  --name "Read Only" \\
  --permissions "read"

# 创建具有特定路由权限的 API Key
openclaw gateway api-key create \\
  --name "Chat Only" \\
  --routes "/api/chat,/api/chat/stream"

# 创建具有速率限制的 API Key
openclaw gateway api-key create \\
  --name "Limited" \\
  --rate-limit 10  # 每分钟 10 次请求`}
      />

      <InfoBox type="warning" title="安全提醒">
        <ul className="space-y-1 mt-2">
          <li>• 不要将 API Key 提交到代码仓库</li>
          <li>• 使用环境变量存储 API Key</li>
          <li>• 定期轮换 API Key</li>
          <li>• 为不同的应用创建独立的 API Key</li>
          <li>• 设置合理的过期时间</li>
        </ul>
      </InfoBox>

      <SectionTitle id="jwt">JWT 认证</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        JWT（JSON Web Token）适合需要用户身份验证的场景，支持无状态认证。
      </p>

      <SubsectionTitle id="jwt-config">配置 JWT 认证</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
auth:
  enabled: true
  type: "jwt"
  jwt:
    # JWT 密钥（用于签名和验证）
    secret: "your-secret-key-min-32-chars"
    # 或使用公钥/私钥对（RS256）
    # public_key_file: "/path/to/public.pem"
    # private_key_file: "/path/to/private.pem"
    
    # 签名算法
    algorithm: "HS256"  # HS256, HS384, HS512, RS256, RS384, RS512
    
    # Token 过期时间（秒）
    expiration: 3600  # 1 小时
    
    # 刷新 Token 过期时间
    refresh_expiration: 604800  # 7 天
    
    # 从哪个 Header 读取 Token
    header: "Authorization"
    prefix: "Bearer"
    
    # Token 签发者
    issuer: "openclaw-gateway"
    
    # Token 受众
    audience: "openclaw-api"`}
      />

      <SubsectionTitle id="jwt-generation">生成 JWT Token</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 用户登录，获取 JWT Token
curl -X POST http://localhost:3000/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "user@example.com",
    "password": "password123"
  }'

# 响应
{
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "refresh_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "token_type": "Bearer",
  "expires_in": 3600
}`}
      />

      <SubsectionTitle id="jwt-usage">使用 JWT Token</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 使用 Access Token 调用 API
curl -X POST http://localhost:3000/api/chat \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Hello"}'

# 刷新 Token
curl -X POST http://localhost:3000/api/auth/refresh \\
  -H "Content-Type: application/json" \\
  -d '{
    "refresh_token": "eyJhbGciOiJIUzI1NiIs..."
  }'`}
      />

      <SubsectionTitle id="jwt-claims">JWT Claims</SubsectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        JWT Token 中可以包含用户信息和权限：
      </p>
      <CodeBlock
        language="json"
        code={`{
  "sub": "user123",  // 用户 ID
  "email": "user@example.com",
  "name": "John Doe",
  "roles": ["user", "admin"],
  "permissions": ["read", "write"],
  "iat": 1704067200,  // 签发时间
  "exp": 1704070800,  // 过期时间
  "iss": "openclaw-gateway",  // 签发者
  "aud": "openclaw-api"  // 受众
}`}
      />

      <SectionTitle id="oauth2">OAuth 2.0</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        OAuth 2.0 适合第三方应用集成，支持多种授权流程。
      </p>

      <SubsectionTitle id="oauth2-config">配置 OAuth 2.0</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
auth:
  enabled: true
  type: "oauth2"
  oauth2:
    # OAuth 提供商
    provider: "github"  # github, google, microsoft, custom
    
    # 客户端凭证
    client_id: "your-client-id"
    client_secret: "your-client-secret"
    
    # 回调地址
    redirect_uri: "http://localhost:3000/api/auth/callback"
    
    # 授权范围
    scopes: ["read:user", "user:email"]
    
    # 自定义提供商配置
    custom:
      auth_url: "https://provider.com/oauth/authorize"
      token_url: "https://provider.com/oauth/token"
      user_info_url: "https://provider.com/api/user"`}
      />

      <SubsectionTitle id="oauth2-flow">OAuth 2.0 授权流程</SubsectionTitle>
      <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
        <ol className="space-y-3 text-slate-300">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-sm font-bold">1</span>
            <div>
              <strong className="text-white">用户点击登录</strong>
              <p className="text-sm text-slate-400 mt-1">应用重定向到 OAuth 提供商的授权页面</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-sm font-bold">2</span>
            <div>
              <strong className="text-white">用户授权</strong>
              <p className="text-sm text-slate-400 mt-1">用户在 OAuth 提供商页面同意授权</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-sm font-bold">3</span>
            <div>
              <strong className="text-white">获取授权码</strong>
              <p className="text-sm text-slate-400 mt-1">OAuth 提供商重定向回应用，携带授权码</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-sm font-bold">4</span>
            <div>
              <strong className="text-white">交换 Token</strong>
              <p className="text-sm text-slate-400 mt-1">应用使用授权码换取 Access Token</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 text-sm font-bold">5</span>
            <div>
              <strong className="text-white">访问资源</strong>
              <p className="text-sm text-slate-400 mt-1">使用 Access Token 调用 API</p>
            </div>
          </li>
        </ol>
      </div>

      <SectionTitle id="mtls">mTLS 认证</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        mTLS（Mutual TLS）提供双向认证，客户端和服务端都需要验证对方的证书，适合企业级安全要求。
      </p>

      <SubsectionTitle id="mtls-config">配置 mTLS</SubsectionTitle>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
auth:
  enabled: true
  type: "mtls"
  mtls:
    # 服务端证书
    cert_file: "/etc/openclaw/ssl/server-cert.pem"
    key_file: "/etc/openclaw/ssl/server-key.pem"
    
    # CA 证书（用于验证客户端证书）
    ca_file: "/etc/openclaw/ssl/ca-cert.pem"
    
    # 客户端证书验证模式
    verify_mode: "require"  # require, optional, none
    
    # 允许的客户端证书 CN（Common Name）
    allowed_cns:
      - "client1.example.com"
      - "client2.example.com"`}
      />

      <SubsectionTitle id="mtls-usage">使用 mTLS</SubsectionTitle>
      <CodeBlock
        language="bash"
        code={`# 使用客户端证书调用 API
curl -X POST https://localhost:3000/api/chat \\
  --cert /path/to/client-cert.pem \\
  --key /path/to/client-key.pem \\
  --cacert /path/to/ca-cert.pem \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Hello"}'`}
      />

      <SectionTitle id="multi-auth">多重认证</SectionTitle>
      <p className="text-slate-300 leading-relaxed mb-4">
        可以同时启用多种认证方式，按优先级依次尝试：
      </p>
      <CodeBlock
        language="yaml"
        code={`# gateway.yaml
auth:
  enabled: true
  # 认证方式优先级（从高到低）
  types: ["jwt", "api_key", "oauth2"]
  
  # 各认证方式的配置
  jwt:
    secret: "your-jwt-secret"
    expiration: 3600
    
  api_key:
    header: "X-API-Key"
    storage: "redis"
    
  oauth2:
    provider: "github"
    client_id: "your-client-id"
    client_secret: "your-client-secret"`}
      />

      <SectionTitle id="auth-best-practices">安全最佳实践</SectionTitle>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i className="ri-shield-check-line text-green-400"></i>
            推荐做法
          </h3>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-green-400 mt-0.5"></i>
              <span>使用 HTTPS 加密传输</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-green-400 mt-0.5"></i>
              <span>定期轮换密钥和证书</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-green-400 mt-0.5"></i>
              <span>设置合理的 Token 过期时间</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-green-400 mt-0.5"></i>
              <span>使用强密码和长密钥</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-green-400 mt-0.5"></i>
              <span>启用限流和熔断</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-check-line text-green-400 mt-0.5"></i>
              <span>记录所有认证失败日志</span>
            </li>
          </ul>
        </div>

        <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <i className="ri-close-circle-line text-red-400"></i>
            避免做法
          </h3>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li className="flex items-start gap-2">
              <i className="ri-close-line text-red-400 mt-0.5"></i>
              <span>在代码中硬编码密钥</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-close-line text-red-400 mt-0.5"></i>
              <span>使用 HTTP 明文传输</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-close-line text-red-400 mt-0.5"></i>
              <span>Token 永不过期</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-close-line text-red-400 mt-0.5"></i>
              <span>共享 API Key 给多个应用</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-close-line text-red-400 mt-0.5"></i>
              <span>忽略证书验证</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-close-line text-red-400 mt-0.5"></i>
              <span>在日志中记录敏感信息</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AuthContent;